/**
 * followUp.ts — Formato legible (es) de la fecha de próximo paso del pipeline,
 * coherente con `next_step_status` que deriva el backend.
 *
 *   - today   → "hoy"
 *   - overdue → "vencido"
 *   - future  → "mañana" | "+N d" | "próx. semana" | "d mmm"
 *
 * Las fechas llegan como 'YYYY-MM-DD' (DATE del backend). Se comparan en horario
 * LOCAL contra hoy para evitar saltos por zona horaria.
 */
import type { NextStepStatus } from '@/services/listingsService'

const MONTHS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']

/** 'YYYY-MM-DD' → Date local a medianoche (sin desfase de zona horaria). */
function parseYmd(due: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(due)
  if (!m) return null
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
}

/** Días de diferencia (entero) entre `due` y hoy, en horario local. */
export function daysUntil(due: string): number | null {
  const d = parseYmd(due)
  if (!d) return null
  const today = new Date()
  const a = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  return Math.round((d.getTime() - a.getTime()) / (24 * 60 * 60 * 1000))
}

/** Texto corto del follow-up para la tarjeta del kanban. */
export function formatFollowUp(due: string | null, status: NextStepStatus): string {
  if (status === 'no_next_step' || !due) return ''
  if (status === 'today') return 'hoy'
  if (status === 'overdue') return 'vencido'
  // future
  const diff = daysUntil(due)
  if (diff === null) return ''
  if (diff === 1) return 'mañana'
  if (diff <= 6) return `+${diff} d`
  if (diff <= 13) return 'próx. semana'
  const d = parseYmd(due)
  return d ? `${d.getDate()} ${MONTHS[d.getMonth()]}` : ''
}

/** Etiqueta de la fecha grande de la ficha (ej. "4 jun"). '' si no hay fecha. */
export function formatDueLong(due: string | null): string {
  if (!due) return ''
  const d = parseYmd(due)
  return d ? `${d.getDate()} ${MONTHS[d.getMonth()]}` : ''
}

/** Palabra grande del próximo paso en la ficha: HOY / VENCIDO / MAÑANA / +N D / fecha. */
export function formatDueHeadline(due: string | null, status: NextStepStatus): string {
  if (status === 'no_next_step' || !due) return ''
  if (status === 'today') return 'HOY'
  if (status === 'overdue') return 'VENCIDO'
  const diff = daysUntil(due)
  if (diff === 1) return 'MAÑANA'
  if (diff !== null && diff <= 6) return `+${diff} D`
  return formatDueLong(due).toUpperCase()
}
